import './EntityTooltip.css';

import * as React from 'react';
import { ReactNode, useCallback, useEffect, useState } from 'react';

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';

interface EntityTooltipProps {
  /** Content to render in the tooltip - can be a function receiving isPersistent */
  content: ReactNode | ((isPersistent: boolean) => ReactNode);

  /** Trigger element */
  children: ReactNode;

  /** Delay in ms before tooltip becomes persistent (default: 1500) */
  persistenceDelay?: number;

  /** Optional: side for tooltip placement */
  side?: 'top' | 'right' | 'bottom' | 'left';

  /** Optional: align for tooltip placement */
  align?: 'start' | 'center' | 'end';

  /** Optional: controlled open state */
  open?: boolean;

  /** Optional: on open change callback */
  onOpenChange?: (open: boolean) => void;

  /** Delay in ms before tooltip opens (default: 200) */
  openDelay?: number;
}

/**
 * EntityTooltip - A hover card that becomes persistent after a delay
 *
 * Inspired by Paradox game tooltips that:
 * - Show on hover
 * - Become "sticky" after hovering for a duration
 * - Allow interaction with nested content
 */
export const EntityTooltip = ({
  content,
  children,
  persistenceDelay = 500,
  side = 'right',
  align = 'center',
  open: controlledOpen,
  onOpenChange,
  openDelay = 200,
}: EntityTooltipProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPersistent, setIsPersistent] = useState(false);
  const persistenceTimerRef = React.useRef<NodeJS.Timeout>();

  // Use controlled or uncontrolled state
  const open = controlledOpen !== undefined ? controlledOpen : isOpen;
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      // Always update internal state for uncontrolled mode
      setIsOpen(newOpen);

      // Also call external callback if provided
      if (onOpenChange) {
        onOpenChange(newOpen);
      }

      // If opening, start persistence timer
      if (newOpen) {
        persistenceTimerRef.current = setTimeout(() => {
          setIsPersistent(true);
        }, persistenceDelay);
      } else {
        // If closing, clear timer and reset state
        if (persistenceTimerRef.current) {
          clearTimeout(persistenceTimerRef.current);
        }
        // Only clear persistence if we're actually closing
        // In controlled mode, we might stay open
        if (!controlledOpen) {
          setIsPersistent(false);
        }
      }
    },
    [onOpenChange, persistenceDelay, controlledOpen],
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (persistenceTimerRef.current) {
        clearTimeout(persistenceTimerRef.current);
      }
    };
  }, []);

  return (
    <HoverCard
      open={open}
      onOpenChange={handleOpenChange}
      openDelay={openDelay}
      closeDelay={isPersistent ? 300 : 150} // Increase close delay significantly when persistent
    >
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        side={side}
        align={align}
        className={`w-72 overflow-hidden rounded-lg border border-gray-200 bg-white/95 p-0 shadow-xl backdrop-blur-xl transition-all duration-300 ${
          isPersistent ? 'ring-1 ring-black/5' : ''
        }`}
        onPointerDownOutside={(e) => {
          // Prevent closing if clicking inside the tooltip (handled by default but good to be explicit)
          const target = e.target as Element;
          if (target.closest('[data-radix-popper-content-wrapper]')) return;

          // Reset persistent state and close
          setIsPersistent(false);
          setIsOpen(false);
          if (onOpenChange) {
            onOpenChange(false);
          }
        }}
        onEscapeKeyDown={() => {
          // Always allow ESC to close
          setIsPersistent(false);
          handleOpenChange(false);
        }}
      >
        <div className="relative">
          {typeof content === 'function' ? content(isPersistent) : content}

          {/* Persistence progress indicator - subtle gray line */}
          <div className="absolute bottom-0 left-0 right-0 h-[3px] overflow-hidden bg-transparent">
            {!isPersistent && (
              <div
                className="h-full w-0 bg-gray-300"
                style={{
                  animation: `progressGrow ${persistenceDelay}ms linear forwards`,
                }}
              />
            )}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};
